<template>
  <div class="h3 mb-3">Сопоставление вариантов размещения</div>
  <p class="paragraph-s_h mb-10">Отметьте варианты размещения, которые хотите использовать</p>

  <e-expansion-panels v-if="isFetchLoading" :model-value="[0]" data-wide-content>
    <e-expansion-panel>
      <template #title>
        <skeleton-loader width="160px" height="24px" />
      </template>

      <e-data-items :model-value="[]" :headers="headers" :items="[]" loading />
    </e-expansion-panel>
  </e-expansion-panels>

  <e-alert v-if="placingVariants?.invalidRoomCategoryCids" variant="error">
    <div class="paragraph-l">Invalid RoomCategoryCIDs:</div>
    <div>
      <span v-for="(cid, index) in placingVariants?.invalidRoomCategoryCids" :key="cid"
        ><span v-if="index !== 0">, </span>{{ cid }}</span
      >
    </div>
  </e-alert>

  <e-form v-else ref="formRef" v-model="isValid" data-wide-content>
    <e-expansion-panels v-for="room in items" :key="room.roomCid" default-opened>
      <e-expansion-panel>
        <template #title> {{ room.roomName[0].value }}</template>
        <e-data-items v-model="room.selected" return-object :headers="headers" :items="room.items" show-select>
          <template v-slot:[`header.data-table-select`]="props">
            <e-checkbox
              :model-value="props.allSelected"
              :square-selected="props.someSelected"
              @update:modelValue="onSelectAllClick(room, $event)"
            />
          </template>

          <template v-slot:[`item.data-table-select`]="{ isSelected, internalItem, toggleSelect, item }">
            <e-checkbox
              :model-value="isSelected(internalItem)"
              :disabled="item.mandatory"
              @update:modelValue="toggleSelect(internalItem, $event)"
            />
          </template>

          <template v-slot:[`item.extranetName`]="{ item, value }">
            <span :class="{ 'with-badge': item.isConflicted }">
              {{ value }}
              <help-tooltip>
                <placing-variants-tooltip :placing-variant="item.placingVariant" />
              </help-tooltip>
            </span>
          </template>

          <template v-slot:[`item.maxNoSeatQuantity`]="{ item, isSelected, internalItem }">
            <e-input
              v-show="isSelected(internalItem)"
              v-model:number-value="item.maxNoSeatQuantity"
              :maska="placesMask"
              :rules="item.mandatory ? [required()] : undefined"
            />
          </template>
        </e-data-items>
      </e-expansion-panel>
    </e-expansion-panels>

    <teleport v-if="isMounted" defer :to="`#${travelLineIntegrationSubmitButtonTeleport}`">
      <e-btn :loading="isLoading" variant="primary" @click="submit"> Завершить</e-btn>
    </teleport>
  </e-form>
</template>

<script setup lang="ts">
import { useMounted } from '@vueuse/core';
import { nextTick, ref } from 'vue';
import { useRouter } from 'vue-router';
import type { ArrayElementType } from '@/core/types/gengeric';
import type { PlacingVariantsRoom, TLPlacingVariant } from '@/parts/tariffs/types/travel-line';
import EDataItems from '@/core/components/common/data-items/EDataItems.vue';
import SkeletonLoader from '@/core/components/interface/SkeletonLoader.vue';
import {
  EAlert,
  EBtn,
  ECheckbox,
  EExpansionPanel,
  EExpansionPanels,
  EForm,
  EInput,
  HelpTooltip,
} from '@/core/components/ui';
import { useFetch } from '@/core/composables/useFetch';
import { placesMask } from '@/core/consts/masks';
import { travelLineIntegrationSubmitButtonTeleport } from '@/core/consts/teleport';
import { RouteNames } from '@/core/plugins/router/routeNames';
import { type DataTableHeaders, EditableHeaderType } from '@/core/types/components/table';
import { isDefined } from '@/core/utils/common';
import { required } from '@/core/utils/validation';
import { ChannelManagerCode } from '@/graphql/generated';
import PlacingVariantsTooltip from '@/parts/tariffs/components/PlacingVariantsTooltip.vue';
import { useTravelLineStore } from '@/parts/tariffs/store/travelLine';

const $router = useRouter();

const { getPlacingVariants, placingVariants, updatePlacingVariants, channelManagerCode } = useTravelLineStore();

const items = ref<PlacingVariantsRoom[]>();

const isValid = ref(false);
const isLoading = ref(false);
const isMounted = useMounted();
const formRef = ref();

const onSelectAllClick = (room: PlacingVariantsRoom, value: boolean) => {
  if (value) {
    room.selected = room.items;
  } else {
    room.selected = room.selected.filter((s) => s.mandatory);
  }
};

const setItems = () => {
  if (!placingVariants.value?.placingVariantsTarification) {
    return;
  }

  items.value = placingVariants.value.placingVariantsTarification.map((room) => {
    const placingVariants: TLPlacingVariant[] = room.conflictedPlacingVariants.map((pv) => ({
      ...pv,
      isConflicted: true,
    }));

    placingVariants.unshift(...room.suggestedPlacingVariants);

    const items = placingVariants.map((placingVariant) => ({
      extranetName: placingVariant.name,
      TLName: placingVariant.mandatory ? placingVariant.name : undefined,
      maxNoSeatQuantity: placingVariant.tarification?.maxNoSeatQuantity || 0,
      isConflicted: placingVariant.isConflicted,
      mandatory: placingVariant.mandatory,
      isChecked: placingVariant.isChecked,
      roomPlacingCID: placingVariant.tarification?.roomPlacingCid,
      defaultAvailable: placingVariant.tarification?.defaultAvailable,
      isNeedConfirm: placingVariant.tarification?.isNeedConfirm,
      pricePerRoom: placingVariant.tarification?.pricePerRoom,
      placingVariant: placingVariant.placingVariant,
    }));

    return {
      roomCid: room.roomCategory.cid,
      roomName: room.roomCategory.name,
      items,
      selected: items.filter((i) => i.mandatory || i.isChecked),
    };
  });
};

const submit = async () => {
  if (!items.value) {
    return;
  }

  formRef.value?.validate();

  await nextTick();

  if (!isValid.value) {
    return;
  }

  isLoading.value = true;

  const result = await updatePlacingVariants(
    items.value
      .filter((i) => i.selected.length)
      .map((item) => ({
        roomCategoryCid: item.roomCid,
        variants: item.selected
          .filter((i) => isDefined(i.maxNoSeatQuantity))
          .map((i) => ({
            roomPlacingCID: i.roomPlacingCID,
            shortName: i.extranetName,
            placings: i.roomPlacingCID
              ? null
              : {
                  baseSeats: i.placingVariant.baseSeats.map((s) => ({
                    seats: s.seats,
                    touristType: {
                      ageCategory: s.touristType.ageCategory
                        ? {
                            from: s.touristType.ageCategory.from,
                            to: s.touristType.ageCategory.to,
                          }
                        : null,
                    },
                  })),
                  extSeats: i.placingVariant.extSeats.map((s) => ({
                    seats: s.seats,
                    touristType: {
                      ageCategory: s.touristType.ageCategory
                        ? {
                            from: s.touristType.ageCategory.from,
                            to: s.touristType.ageCategory.to,
                          }
                        : null,
                    },
                  })),
                },
            defaultAvailable: i.defaultAvailable || false,
            pricePerRoom: i.pricePerRoom || false,
            isNeedConfirm: i.isNeedConfirm || false,
            maxNoSeatQuantity: i.maxNoSeatQuantity,
          })),
      })),
  );

  if (result?.data) {
    $router.push({
      name: RouteNames.TariffInfo,
      params: {
        t_id: result.data.travelLineUpdatePlacingVariants.View!.tariffScheduleCID,
      },
    });
  }

  isLoading.value = false;
};

const headers: DataTableHeaders<ArrayElementType<PlacingVariantsRoom['items']>> = [
  {
    key: 'TLName',
    title:
      channelManagerCode.value === ChannelManagerCode.TravelLine ? 'Вариант размещения TL' : 'Вариант размещения Exely',
  },
  {
    key: 'extranetName',
    title: 'Вариант размещения Экстранет',
  },
  {
    key: 'maxNoSeatQuantity',
    title: 'Туристы без места',
    sortable: false,
    editable: {
      type: EditableHeaderType.Input,
      title: 'Туристы без места',
      editableKey: 'maxNoSeatQuantity',
      maska: placesMask,
    },
  },
];

const { isLoading: isFetchLoading } = useFetch(async () => {
  await getPlacingVariants();
  setItems();
});
</script>

<style scoped lang="scss"></style>
